/**
 * FeatureShowcase — multi-tab accordion showing all platform features per persona.
 * Displayed on the login page as a guided product tour.
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Globe, Building2, User, ChevronDown, UsersRound,
  LayoutDashboard, FileText, Mail, HardDrive, ShieldCheck, FileSignature,
  ClipboardList, Coins, Key, Receipt, Clock, Users, RefreshCw,
  Scale, Settings, TrendingUp, Wrench, Palette, CreditCard,
  LogIn, Smartphone, CheckCircle, CalendarDays, FolderOpen, Download,
  UserCheck, Banknote, ShieldAlert, Gavel, BarChart3, AlertTriangle,
  ArrowRightLeft, Snowflake, BookOpen,
} from 'lucide-react';

const IMAGES = {
  sysadmin_tenant_mgmt: 'https://static.prod-images.emergentagent.com/jobs/182c820b-d3ce-4a0b-9bbb-2536580ff027/images/9a2f755fd6c6ddf169c8d1bbfcf49ffeb035750126586081d1a9eba8751149c3.png',
  sysadmin_email_templates: 'https://static.prod-images.emergentagent.com/jobs/182c820b-d3ce-4a0b-9bbb-2536580ff027/images/1db9cbca3efc055d53aa92c2abc27d3ce70e268e0c8aa4657a63491863e4228b.png',
  sysadmin_agreement_templates: 'https://static.prod-images.emergentagent.com/jobs/182c820b-d3ce-4a0b-9bbb-2536580ff027/images/5b9b469e38eb2036dbcc310123eaa35f82d031cbbb7f66745e3977f4e699d15f.png',
  sysadmin_platform_config: 'https://static.prod-images.emergentagent.com/jobs/182c820b-d3ce-4a0b-9bbb-2536580ff027/images/1009cab41f48ba05388620ec7a59b481fe70dea50c0de390076aa389d90394db.png',
  tenant_dashboard: 'https://static.prod-images.emergentagent.com/jobs/182c820b-d3ce-4a0b-9bbb-2536580ff027/images/0ddac20f8488ca7f20fc2228989a102819c380529beff8d643e6543f7290563a.png',
  tenant_loan_origination: 'https://static.prod-images.emergentagent.com/jobs/182c820b-d3ce-4a0b-9bbb-2536580ff027/images/4c7f60de783bafd424f8d86aa031055e74f760fa78394da29b5e8a077d742d86.png',
  tenant_esign_workflow: 'https://static.prod-images.emergentagent.com/jobs/182c820b-d3ce-4a0b-9bbb-2536580ff027/images/cc87ff71082b2be9d3fc548252628a6ab06082ca6eebfbf302c1e3c6c0afc0fb.png',
  tenant_compliance: 'https://static.prod-images.emergentagent.com/jobs/182c820b-d3ce-4a0b-9bbb-2536580ff027/images/bfc2312bd71301cecfc3a6c87ccead3679c5f9767c14ad91d238ba976212a5e9.png',
  tenant_integrations: 'https://static.prod-images.emergentagent.com/jobs/182c820b-d3ce-4a0b-9bbb-2536580ff027/images/7be8a1b9007252081c54405c5580f94785524e6d173e68a29efabf9a0c6a2d82.png',
  tenant_security_collateral: 'https://static.prod-images.emergentagent.com/jobs/182c820b-d3ce-4a0b-9bbb-2536580ff027/images/5aae84d65caed52a3738a8ba959634695a68aa4a8ae478206dc6f1b6a0bdcbe9.png',
  borrower_dashboard: 'https://static.prod-images.emergentagent.com/jobs/182c820b-d3ce-4a0b-9bbb-2536580ff027/images/e585c953745bc3dca6328e03cc97f754e7aafafdd5de189532b4d50c0ff98384.png',
  borrower_loan_detail: 'https://static.prod-images.emergentagent.com/jobs/182c820b-d3ce-4a0b-9bbb-2536580ff027/images/c139bb687e049d5d027b8c892eabee413165a8e11f58241efe3354e3fbc6b490.png',
  // New batch — Lender Team
  loan_officer_applications: 'https://static.prod-images.emergentagent.com/jobs/d666cbdc-1c32-4c63-8055-3f41f1e8d9fe/images/59c3ed684d6e8a92dc9f125de714fe27694eedbdf31b54b47ea2c6199bf5a1f6.png',
  credit_manager_approvals: 'https://static.prod-images.emergentagent.com/jobs/d666cbdc-1c32-4c63-8055-3f41f1e8d9fe/images/234f8df9cdffe6e31fddc3b1e570d3a92568fd432bb3f32cf22c3a569031d820.png',
  risk_manager_dashboard: 'https://static.prod-images.emergentagent.com/jobs/d666cbdc-1c32-4c63-8055-3f41f1e8d9fe/images/5a374b86562b8e7061dedeb89dd7005a4fb24d3d5d3894acd3650d0edae250b6.png',
  collections_manager_recovery: 'https://static.prod-images.emergentagent.com/jobs/d666cbdc-1c32-4c63-8055-3f41f1e8d9fe/images/7a40c29cd180888f8c0ed6dfb2b144d2f0185cc3f070e2f0d5c873979f749b80.png',
  // New batch — Existing tab gaps
  tenant_loan_detail: 'https://static.prod-images.emergentagent.com/jobs/d666cbdc-1c32-4c63-8055-3f41f1e8d9fe/images/d94b4b6a60e4dbf42ea052b815fc0997e60bcf230ad0132f7e0552284ad92ede.png',
  tenant_kfs_approval: 'https://static.prod-images.emergentagent.com/jobs/d666cbdc-1c32-4c63-8055-3f41f1e8d9fe/images/263398503bd2d40b3bef135e2acd5a31d4910ad70b822956e14d936f49335429.png',
  borrower_repayment_schedule: 'https://static.prod-images.emergentagent.com/jobs/d666cbdc-1c32-4c63-8055-3f41f1e8d9fe/images/530977a26bf6d6089745d9bf5e07fe0a01a380312aeb61ad66c3dd62bb9c5ddc.png',
  borrower_esign_flow: 'https://static.prod-images.emergentagent.com/jobs/d666cbdc-1c32-4c63-8055-3f41f1e8d9fe/images/99137dec4b6711a8bbf784ebaed1c3fe924cdcea09f9c3add62e552e885e3d65.png',
  tenant_demands_overdue: 'https://static.prod-images.emergentagent.com/jobs/d666cbdc-1c32-4c63-8055-3f41f1e8d9fe/images/c0555f8736dadde299e5eb545cc494cf47f8c1cbb9a65e8f4ee5e968e8c56752.png',
  tenant_team_branding: 'https://static.prod-images.emergentagent.com/jobs/d666cbdc-1c32-4c63-8055-3f41f1e8d9fe/images/9063de654a4cc8b314facd592f7ad5bc26f2fb3dd94cc7b00cad435525eef11d.png',
};

const PERSONAS = {
  system_admin: {
    label: 'System Admin',
    icon: Globe,
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    description: 'Platform-wide configuration, tenant management, and master templates',
    steps: [
      { icon: Globe, title: 'Tenant Management', desc: 'Create, activate, and manage all lending companies on the platform. Monitor tenant health, subscription status, and credit usage from a centralized dashboard.', image: IMAGES.sysadmin_tenant_mgmt },
      { icon: CreditCard, title: 'Credit System & Billing', desc: 'Configure platform-wide credit pricing, allocate credits to tenants, and track consumption. Tenants use credits for API calls, document generation, and email sending.' },
      { icon: Mail, title: 'Email Infrastructure', desc: 'Configure SMTP settings for the entire platform. Create and manage master email templates that all tenants inherit — from user invitations to loan lifecycle notifications.', image: IMAGES.sysadmin_email_templates },
      { icon: FileSignature, title: 'Master Agreement Templates', desc: 'Design HTML+CSS agreement templates with 30+ auto-resolved variables (borrower name, loan amount, EMI, etc.). Tenants can inherit these or create their own customized versions.', image: IMAGES.sysadmin_agreement_templates },
      { icon: HardDrive, title: 'Storage Configuration (S3)', desc: 'Set up AWS S3 bucket configuration for tenant-scoped document and agreement PDF storage. All files are organized by tenant ID for complete data isolation.' },
      { icon: ShieldCheck, title: 'Security: CAPTCHA & Platform Settings', desc: 'Configure Google reCAPTCHA to protect login and registration forms. Manage platform-level security policies, password rules, and access controls.', image: IMAGES.sysadmin_platform_config },
    ],
  },
  tenant_admin: {
    label: 'Tenant Admin',
    icon: Building2,
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    description: 'Full lending operations — origination, management, compliance, and team admin',
    steps: [
      { icon: LayoutDashboard, title: 'Dashboard & Portfolio Overview', desc: 'Real-time KPIs: total loans, active portfolio, disbursement trends, and collection performance. Visual charts for portfolio health monitoring and quick-action cards.', image: IMAGES.tenant_dashboard },
      { icon: ClipboardList, title: 'Loan Origination & Applications', desc: 'End-to-end loan origination: create applications with borrower details, select loan products, upload KYC documents, and submit for approval. Multi-step workflow with document checklist.', image: IMAGES.tenant_loan_origination },
      { icon: CheckCircle, title: 'Loan Approvals & KFS', desc: 'Review pending applications with a comprehensive Key Fact Statement (KFS) showing APR, total cost of credit, and all charges — ensuring full regulatory transparency before approval.', image: IMAGES.tenant_kfs_approval },
      { icon: FileText, title: 'Loan Management & Lifecycle', desc: 'Manage the full loan lifecycle: view all loans with filters, drill into individual loans with detailed tabs (overview, schedule, repayments, documents, charges). Track disbursements and repayments.', image: IMAGES.tenant_loan_detail },
      { icon: FileSignature, title: 'Agreement Generation & eSign', desc: 'Generate loan agreements from templates with auto-filled variables. Send for digital signing via Leegality eSign — borrowers receive a signing link and you can track status in real time.', image: IMAGES.tenant_esign_workflow },
      { icon: Coins, title: 'Disbursements & Repayments', desc: 'Process loan disbursements (full or partial), record repayments manually or via bulk upload. Automatic demand generation and payment application with configurable offset order.' },
      { icon: Key, title: 'Security & Collateral Management', desc: 'Track pledged securities (gold, property, FD, vehicles). Manage security assignments, releases, market price updates, and automated shortfall detection with LTV monitoring.', image: IMAGES.tenant_security_collateral },
      { icon: Receipt, title: 'Charges, Interest & Adjustments', desc: 'Configure charge types (processing fees, late charges, insurance). Run daily interest accruals, apply adjustments and waivers. Full audit trail for all financial operations.' },
      { icon: Clock, title: 'Demands, Overdue & Collections', desc: 'Auto-generated loan demands with configurable schedules. Overdue tracking with aging analysis, waiver processing, and payment application. Demand offset order configuration.', image: IMAGES.tenant_demands_overdue },
      { icon: Users, title: 'Co-Lending & Loan Transfers', desc: 'Manage co-lending partnerships: define partner splits, track partner-wise portfolio, and process loan transfers between entities — complete with regulatory documentation.' },
      { icon: RefreshCw, title: 'Loan Operations: Restructure, Write-Off, Refund', desc: 'Handle complex operations: restructure distressed loans with new terms, process write-offs with approval workflows, and manage refunds for overpayments or closures.' },
      { icon: Scale, title: 'Regulatory Compliance', desc: 'CIBIL CDS v2.0 reporting with auto-generated monthly files and borrower-level reminders. Cooling-off period enforcement. Loan cancellation processing with regulatory audit trails.', image: IMAGES.tenant_compliance },
      { icon: TrendingUp, title: 'Risk Dashboard & Reports', desc: 'Risk analysis: portfolio concentration, NPA tracking, and exposure monitoring. Comprehensive reports hub with loan-book, disbursement, collection, and compliance reports.' },
      { icon: Settings, title: 'Integrations: SMS, eSign', desc: 'Configure third-party integrations: AWS SNS for SMS OTP, Leegality for digital signatures. One-click test connectivity for each service.', image: IMAGES.tenant_integrations },
      { icon: Wrench, title: 'Tools: Bulk Operations & Import', desc: 'Power tools: bulk repayment upload via CSV, repost tools for recalculating demands/interest, demand offset order configuration, and data import utilities.' },
      { icon: Palette, title: 'Company Admin: Team, Branding & Email', desc: 'Manage team members with role-based access. Customize company branding (logo, colors). Customize email templates and monitor all email delivery logs.', image: IMAGES.tenant_team_branding },
    ],
  },
  lender_team: {
    label: 'Lender Team',
    icon: UsersRound,
    color: 'text-violet-600',
    bg: 'bg-violet-50',
    border: 'border-violet-200',
    description: 'Specialized roles for day-to-day lending operations — each with focused permissions',
    roles: [
      {
        role: 'Loan Officer',
        icon: ClipboardList,
        color: 'text-green-600',
        desc: 'Originates and manages loan applications, borrower onboarding, and document collection',
        steps: [
          { icon: ClipboardList, title: 'Create & Manage Applications', desc: 'Build loan applications with a multi-step wizard: applicant details, employment, identity documents, loan requirements, and final review. Auto-numbering (APP-YYYYMM-NNNN) and status tracking throughout.', image: IMAGES.loan_officer_applications },
          { icon: FolderOpen, title: 'Document Upload & KYC', desc: 'Upload borrower KYC documents (ID proof, address proof, income proof) to secure S3 storage. Configure required document types per loan product. File validation with size and type restrictions.' },
          { icon: Users, title: 'Co-Applicant Management', desc: 'Add and manage multiple co-applicants per loan application with their own identity and employment details. Required for joint loans and guarantor documentation.' },
          { icon: ArrowRightLeft, title: 'Workflow Transitions', desc: 'Move applications through the origination pipeline: Draft, Submit, Under Review. Each transition is tracked with user, timestamp, and remarks for full audit trail.' },
        ],
      },
      {
        role: 'Credit Manager',
        icon: UserCheck,
        color: 'text-purple-600',
        desc: 'Approves or rejects applications, reviews KFS, and manages disbursements',
        steps: [
          { icon: CheckCircle, title: 'Application Approvals', desc: 'Review submitted applications with full borrower details, income verification, and document completeness. Approve at manager or senior level, or reject with documented reasons.', image: IMAGES.credit_manager_approvals },
          { icon: BookOpen, title: 'KFS Review & APR Verification', desc: 'Review the auto-generated Key Fact Statement showing IRR-based APR calculation, total cost of credit, fee breakdowns, and cooling-off period. Compare up to 4 fee scenarios side-by-side.' },
          { icon: Banknote, title: 'Disbursement Processing', desc: 'Create and post loan disbursements — full or partial (multi-tranche). Record mode (NEFT, RTGS, cheque), reference numbers, and dates. Automatic email notification to borrower on posting.' },
          { icon: FileSignature, title: 'Loan Sanctioning', desc: 'Final approval step: sanction the application which auto-creates the loan record with generated loan number, repayment schedule, and all configured terms from the loan product.' },
        ],
      },
      {
        role: 'Risk Manager',
        icon: ShieldAlert,
        color: 'text-orange-600',
        desc: 'Monitors portfolio risk, NPA classification, and regulatory provisioning',
        steps: [
          { icon: BarChart3, title: 'NPA Classification & IRAC', desc: 'Define DPD-based NPA classifications (Standard, SMA-0/1/2, Sub-Standard, Doubtful, Loss) with RBI-compliant provisioning percentages. One-click initialization of default RBI structure.', image: IMAGES.risk_manager_dashboard },
          { icon: AlertTriangle, title: 'DPD & Overdue Monitoring', desc: 'Track Days Past Due for every loan in real time. View DPD distribution charts (0-30, 31-60, 61-90, 90+ days). Historical DPD and NPA status transition logs for audit.' },
          { icon: Snowflake, title: 'Loan Freeze & Limit Control', desc: 'Temporarily freeze loan processing for flagged accounts. Log and track changes to loan limits. All actions are audited with user, timestamp, and reason.' },
          { icon: TrendingUp, title: 'Bulk Interest Rate Changes', desc: 'Execute interest rate changes across all active loans in a single background operation. Track process number, execution date, new rate, and total loans affected.' },
        ],
      },
      {
        role: 'Collections Manager',
        icon: Gavel,
        color: 'text-red-600',
        desc: 'Handles restructuring, write-offs, refunds, and overdue loan recovery',
        steps: [
          { icon: RefreshCw, title: 'Loan Restructuring', desc: 'Create restructure requests with new terms (rate, tenure, EMI). Submit for approval and apply to update loan terms. Track all restructures with auto-numbering (RST-YYYYMM-NNNNN).', image: IMAGES.collections_manager_recovery },
          { icon: Scale, title: 'Write-Off Processing', desc: 'Process irrecoverable loan write-offs with multi-step approval workflow. Track written-off amounts, reasons, and approval chain. Full audit trail for regulatory compliance.' },
          { icon: Coins, title: 'Refund Management', desc: 'Record and process refund transactions for excess payments, overpayments, or loan closures. Track refund mode (NEFT/RTGS/IMPS/UPI/Cheque), reference, and settlement dates.' },
          { icon: Clock, title: 'Overdue Recovery & Waivers', desc: 'Monitor overdue loans with aging analysis. Process demand waivers for penalties or charges. Configure payment application priority order (interest-first vs. principal-first).' },
        ],
      },
    ],
  },
  borrower: {
    label: 'Borrower',
    icon: User,
    color: 'text-teal-600',
    bg: 'bg-teal-50',
    border: 'border-teal-200',
    description: 'Self-service portal — view loans, track payments, sign agreements',
    steps: [
      { icon: LogIn, title: 'Email Login', desc: 'Borrowers sign in with the email linked to their loan account — no passwords to remember. The system matches your email to your loan records and identifies which lender(s) you have accounts with.' },
      { icon: Building2, title: 'Tenant Selection (Multi-Lender)', desc: 'If you have loans with multiple lenders on the platform, you\'re prompted to choose which lender\'s portal to access. Each lender\'s data is completely isolated and secure.' },
      { icon: Smartphone, title: 'SMS OTP Verification (2FA)', desc: 'A 6-digit OTP is sent to your registered phone number via SMS for additional security. Rate-limited to prevent abuse (max 5 OTPs per 15 minutes per phone number).' },
      { icon: LayoutDashboard, title: 'Dashboard: Loan Summary & Next EMI', desc: 'Your personalized dashboard showing total loans, active loans, outstanding balance, and total disbursed. A highlighted card shows your next EMI due date and amount for quick reference.', image: IMAGES.borrower_dashboard },
      { icon: FileText, title: 'Loan Details: 5-Tab Deep Dive', desc: 'Click any loan to see five detailed tabs — Overview (key figures), Schedule (full EMI table), Payments (transaction history), Documents (uploaded files), and Agreement (signing status).', image: IMAGES.borrower_loan_detail },
      { icon: CalendarDays, title: 'Repayment Schedule & Payment History', desc: 'View your complete EMI schedule with principal, interest, and balance breakdowns. Track which EMIs are paid, upcoming, or overdue. See all payment transactions with references and status.', image: IMAGES.borrower_repayment_schedule },
      { icon: FolderOpen, title: 'Documents & KYC', desc: 'Access all documents associated with your loan — KYC files, income proofs, sanction letters, and any other uploaded documents. Download them directly from the portal.' },
      { icon: FileSignature, title: 'Digital Agreement Signing', desc: 'When your lender sends a loan agreement for signing, you\'ll see a "Sign Agreement" button that opens Leegality\'s secure signing page. Once signed, both you and the lender receive email + SMS confirmation.', image: IMAGES.borrower_esign_flow },
      { icon: Download, title: 'Download Signed Agreement', desc: 'After signing, the signed copy of your agreement (with digital certificate) is available for download directly from your portal. You also receive a confirmation email with the details.' },
    ],
  },
};

function StepAccordion({ step, index, isOpen, onToggle }) {
  return (
    <div className="border rounded-lg overflow-hidden transition-colors" data-testid={`step-${index}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-3 sm:p-4 text-left hover:bg-slate-50/50 transition-colors"
        data-testid={`step-toggle-${index}`}
      >
        <span className="flex-shrink-0 w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-slate-900 text-white text-xs sm:text-sm font-semibold flex items-center justify-center">
          {index + 1}
        </span>
        <step.icon className="h-4 w-4 text-slate-500 flex-shrink-0" />
        <span className="flex-1 font-medium text-sm sm:text-[15px] leading-snug">{step.title}</span>
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform duration-200 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="px-3 sm:px-4 pb-4 pt-0 space-y-3">
          <p className="text-sm text-muted-foreground leading-relaxed pl-9 sm:pl-10">{step.desc}</p>
          {step.image && (
            <div className="pl-9 sm:pl-10">
              <img
                src={step.image}
                alt={step.title}
                className="rounded-lg border shadow-sm w-full max-w-lg"
                loading="lazy"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

StepAccordion.propTypes = {
  step: PropTypes.shape({
    icon: PropTypes.elementType.isRequired,
    title: PropTypes.string.isRequired,
    desc: PropTypes.string.isRequired,
    image: PropTypes.string,
  }).isRequired,
  index: PropTypes.number.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

function LenderTeamContent({ persona, openSteps, toggleStep }) {
  return (
    <>
      <div className={`rounded-xl ${persona.bg} ${persona.border} border p-3 sm:p-4 mb-5`}>
        <div className="flex items-center gap-2">
          <persona.icon className={`h-5 w-5 ${persona.color}`} />
          <span className={`font-semibold text-sm ${persona.color}`}>{persona.label}</span>
        </div>
        <p className="text-sm text-muted-foreground mt-1">{persona.description}</p>
        <p className="text-xs text-muted-foreground mt-0.5">4 specialized roles</p>
      </div>

      <div className="space-y-6">
        {persona.roles.map((role) => {
          const roleKey = role.role.toLowerCase().replace(/\s+/g, '_');
          return (
            <div key={roleKey} data-testid={`role-${roleKey}`}>
              <div className="flex items-center gap-2 mb-3 px-1">
                <role.icon className={`h-4.5 w-4.5 ${role.color}`} />
                <h3 className={`font-semibold text-[15px] ${role.color}`}>{role.role}</h3>
                <span className="text-xs text-muted-foreground">— {role.desc}</span>
              </div>
              <div className="space-y-2" data-testid={`steps-${roleKey}`}>
                {role.steps.map((step, i) => (
                  <StepAccordion
                    key={step.title}
                    step={step}
                    index={i}
                    isOpen={openSteps[roleKey] === i}
                    onToggle={() => toggleStep(roleKey, i)}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

LenderTeamContent.propTypes = {
  persona: PropTypes.shape({
    icon: PropTypes.elementType.isRequired,
    label: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    bg: PropTypes.string.isRequired,
    border: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    roles: PropTypes.array.isRequired,
  }).isRequired,
  openSteps: PropTypes.object.isRequired,
  toggleStep: PropTypes.func.isRequired,
};

export default function FeatureShowcase() {
  const [activeTab, setActiveTab] = useState('tenant_admin');
  const [openSteps, setOpenSteps] = useState({
    system_admin: 0, tenant_admin: 0, borrower: 0,
    loan_officer: 0, credit_manager: 0, risk_manager: 0, collections_manager: 0,
  });

  const toggleStep = (persona, index) => {
    setOpenSteps(prev => ({
      ...prev,
      [persona]: prev[persona] === index ? -1 : index,
    }));
  };

  return (
    <section className="w-full bg-white border-t" data-testid="feature-showcase">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <div className="text-center mb-8 sm:mb-10">
          <p className="text-xs font-semibold tracking-widest uppercase text-slate-400 mb-2">Platform Walkthrough</p>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">
            Everything you can do with LMS
          </h2>
          <p className="text-muted-foreground text-sm sm:text-base mt-2 max-w-xl mx-auto">
            Explore features available for each role — from platform administration to borrower self-service.
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex justify-center mb-6">
            <TabsList className="grid grid-cols-4 w-full max-w-2xl">
              {Object.entries(PERSONAS).map(([key, persona]) => (
                <TabsTrigger
                  key={key}
                  value={key}
                  className="flex items-center gap-1.5 text-xs sm:text-sm"
                  data-testid={`persona-tab-${key}`}
                >
                  <persona.icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  <span className="hidden sm:inline">{persona.label}</span>
                  <span className="sm:hidden">
                    {key === 'lender_team' ? 'Team' : persona.label.split(' ')[0]}
                  </span>
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          {Object.entries(PERSONAS).map(([key, persona]) => (
            <TabsContent key={key} value={key} className="mt-0">
              {key === 'lender_team' ? (
                <LenderTeamContent
                  persona={persona}
                  openSteps={openSteps}
                  toggleStep={toggleStep}
                />
              ) : (
                <>
                  <div className={`rounded-xl ${persona.bg} ${persona.border} border p-3 sm:p-4 mb-5`}>
                    <div className="flex items-center gap-2">
                      <persona.icon className={`h-5 w-5 ${persona.color}`} />
                      <span className={`font-semibold text-sm ${persona.color}`}>{persona.label}</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{persona.description}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{persona.steps.length} features</p>
                  </div>

                  <div className="space-y-2" data-testid={`steps-${key}`}>
                    {persona.steps.map((step, i) => (
                      <StepAccordion
                        key={step.title}
                        step={step}
                        index={i}
                        isOpen={openSteps[key] === i}
                        onToggle={() => toggleStep(key, i)}
                      />
                    ))}
                  </div>
                </>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </section>
  );
}
